import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, ArrowRight, CheckCircle2 } from "lucide-react";

import { FoxMark, Wordmark } from "@/components/Logo";
import useDocumentMeta from "@/lib/useDocumentMeta";

const fadeUp = {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, ease: "easeOut" },
};

export default function Login() {
    useDocumentMeta({
        title: "Sign in - RecipeFox AI",
        description: "Sign in to RecipeFox AI to manage your AI-generated recipe queue and WordPress publishing.",
        noIndex: true,
    });
    const { login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            nav("/generate");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-paper" data-testid="login-page">
            {/* Left panel - editorial brand side. Visible on lg+. */}
            <div className="hidden lg:flex w-[44%] bg-ink text-paper p-14 flex-col justify-between relative overflow-hidden">
                {/* Subtle grid overlay */}
                <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
                    style={{
                        backgroundImage: "linear-gradient(#FDFBF7 1px, transparent 1px), linear-gradient(90deg, #FDFBF7 1px, transparent 1px)",
                        backgroundSize: "32px 32px",
                    }} />
                <motion.div {...fadeUp}>
                    <Link to="/" className="flex items-center gap-3 group">
                        <FoxMark size={42} color="#FDFBF7" />
                        <Wordmark className="text-paper text-xl group-hover:text-klein transition-colors" />
                    </Link>
                </motion.div>

                <motion.div {...fadeUp} transition={{ duration: 0.55, delay: 0.1 }}>
                    <div className="overline text-paper/60 mb-6">Sign in / 01</div>
                    <h1 className="heading text-5xl xl:text-6xl leading-[0.95]">
                        Production-grade{" "}<br />recipe content,{" "}<br />
                        <span className="text-klein">on autopilot.</span>
                    </h1>
                    <ul className="mt-12 space-y-3 text-sm">
                        {[
                            "Claude Sonnet 5.5 + Seedream 5",
                            "WP Recipe Maker + WP Delicious",
                            "Bulk queue, scheduling, internal linking",
                            "AI-cliche detection + AEO/GEO SEO",
                        ].map((s) => (
                            <li key={s} className="flex items-center gap-3 text-paper/80">
                                <CheckCircle2 className="w-4 h-4 text-klein flex-shrink-0" />
                                <span>{s}</span>
                            </li>
                        ))}
                    </ul>
                </motion.div>

                <motion.div {...fadeUp} transition={{ duration: 0.55, delay: 0.2 }}
                    className="border-l-2 border-klein pl-4 max-w-sm">
                    <p className="text-sm leading-relaxed text-paper/90 italic">
                        &ldquo;Saves me four hours per recipe. The SEO bones are baked in - I just review and publish.&rdquo;
                    </p>
                    <p className="text-xs font-mono text-paper/50 mt-2">- food blogger, 31k MAU</p>
                </motion.div>
            </div>

            {/* Right panel - sign-in form */}
            <div className="flex-1 flex flex-col">
                <div className="lg:hidden p-6 border-b border-line">
                    <Link to="/" className="flex items-center gap-3">
                        <FoxMark size={32} color="#111111" />
                        <Wordmark className="text-base" />
                    </Link>
                </div>
                <motion.div {...fadeUp} transition={{ duration: 0.55, delay: 0.15 }}
                    className="flex-1 flex items-center justify-center p-8">
                    <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
                        <div>
                            <div className="overline">Sign in</div>
                            <h2 className="heading text-3xl mt-2">Welcome back.</h2>
                            <p className="text-sm text-ink2 mt-2">
                                Glad you&apos;re here. Let&apos;s get your queue moving.
                            </p>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="email" className="overline">Email</Label>
                                <Input id="email" type="email" required value={email}
                                    autoFocus autoComplete="email"
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="mt-1.5 rounded-none border-line h-11 focus-visible:border-klein focus-visible:ring-0"
                                    data-testid="login-email-input" />
                            </div>
                            <div>
                                <div className="flex items-baseline justify-between">
                                    <Label htmlFor="password" className="overline">Password</Label>
                                    <Link to="/contact" className="text-[10px] text-ink2 hover:text-klein font-mono uppercase tracking-wider">
                                        Forgot?
                                    </Link>
                                </div>
                                <div className="relative mt-1.5">
                                    <Input id="password" type={show ? "text" : "password"} required value={password}
                                        autoComplete="current-password"
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="rounded-none border-line h-11 pr-11 focus-visible:border-klein focus-visible:ring-0"
                                        data-testid="login-password-input" />
                                    <button type="button" onClick={() => setShow(!show)}
                                        className="absolute right-0 top-0 h-full w-11 flex items-center justify-center text-ink2 hover:text-ink"
                                        aria-label={show ? "Hide password" : "Show password"}
                                        data-testid="login-password-toggle">
                                        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <Button type="submit" disabled={loading}
                            className="w-full h-11 rounded-none bg-klein hover:bg-kleinDark text-white inline-flex items-center justify-center gap-2 transition-colors"
                            data-testid="login-submit-button">
                            {loading ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Signing in.</>
                            ) : (
                                <>Sign in <ArrowRight className="w-4 h-4" /></>
                            )}
                        </Button>
                        <div className="text-sm text-ink2 text-center border-t border-line pt-5">
                            New here?{" "}
                            <Link to="/register" className="text-klein font-semibold hover:underline"
                                data-testid="login-go-register-link">
                                Create a free account
                            </Link>
                        </div>
                    </form>
                </motion.div>
                <div className="p-6 text-[10px] font-mono uppercase text-ink2 text-center border-t border-line">
                    By signing in you agree to our{" "}
                    <Link to="/terms" className="hover:text-klein">Terms</Link> and{" "}
                    <Link to="/privacy" className="hover:text-klein">Privacy Policy</Link>.
                </div>
            </div>
        </div>
    );
}
